import icStatusImage from '@/assets/icons/statusImage.svg';
import icTransactions from '@/assets/icons/transactions.svg';
import icSupport from '@/assets/icons/support.svg';
import icDebits from '@/assets/icons/debits.svg';
import icAdditives from '@/assets/icons/additives.svg';
import icCorrespondences from '@/assets/icons/correspondences.svg';
import icFundSettings from '@/assets/icons/fundSettings.svg';
import icSettingsGear from '@/assets/icons/settingsGear.svg';
import icApprovalsDocuments from '@/assets/icons/approvalsDocuments.svg';
import icDonations from '@/assets/icons/donations.svg';
import icHelp from '@/assets/icons/help.svg';
import icStatusImageActive from '@/assets/icons/statusImage-active.svg';
import icTransactionsActive from '@/assets/icons/transactions-active.svg';
import icSupportActive from '@/assets/icons/support-active.svg';
import icDebitsActive from '@/assets/icons/debits-active.svg';
import icAdditivesActive from '@/assets/icons/additives-active.svg';
import icCorrespondencesActive from '@/assets/icons/correspondences-active.svg';
import icFundSettingsActive from '@/assets/icons/fundSettings-active.svg';
import icSettingsGearActive from '@/assets/icons/settingsGear-active.svg';
import icApprovalsDocumentsActive from '@/assets/icons/approvalsDocuments-active.svg';
import icDonationsActive from '@/assets/icons/donations-active.svg';
import icHelpActive from '@/assets/icons/help-active.svg';
import icProjects from '@/assets/icons/projects.svg';
import icProjectsActive from '@/assets/icons/projects-active.svg';
import icKollels from '@/assets/icons/kollels-sidebar.svg';
import icKollelsActive from '@/assets/icons/kollels-sidebar-active.svg';
import idIcon from '@/assets/icons/id2.svg';

export interface SidebarItem {
  text: string;
  iconSrc?: string;
  activeIconSrc?: string;
  to?: string;
  children?: SidebarItem[];
  /**
   * If true, this item will only be visible to admin users.
   *
   * Important behavior:
   * - If a parent item has adminOnly: true, all its children will be hidden from non-admin users,
   *   regardless of their individual adminOnly values. This is because if the parent is filtered out,
   *   there's no way to access its children.
   * - If a parent item does not have adminOnly: true, children can be filtered individually.
   *   Some children can be adminOnly: true (visible only to admins) while others are visible to all users.
   */
  adminOnly?: boolean;
  /**
   * If true, this item is visible to users with the Debugger role.
   * Combines with adminOnly as an OR: if both are set, the item is visible to
   * Admins OR Debuggers (not only to users who are both).
   */
  debuggerOnly?: boolean;
  /** Key used to look up a dynamic badge count from the SidebarBadgeProvider */
  badgeKey?: string;
  /** Shown beside the count, to say what it counts (HAT-1428). In the icon rail the badge is a dot. */
  badgeIcon?: string;
  /** An i18n key with _one/_other that says in words what the badge counts - its bubble on hover. */
  badgeTooltip?: string;
  /**
   * TEMPORARY: when set, the item is shown only to these emails (lowercase).
   * Used for screens that are live on the site but still in design review, so
   * they must not reach fund coordinators. Delete the list when the screen ships.
   */
  allowedEmails?: readonly string[];
  /**
   * הסתרה מלאה, ולא הטיפול הרגיל של פריט מנוטרל (מוצג מטושטש ולא לחיץ), באתר
   * שהנתיב שלו אינו ב-VITE_APP_ENABLED_ROUTES.
   *
   * הפריט המטושטש מכוון לפריטים שמפרסמים מה עוד יבוא, אבל הוא לא נכון למסך
   * שנמצא בפיתוח: הוא מציב רשומה מתה בתפריט של משתמשי ייצור. נדלק פר פריט
   * כדי שלא ישתנה שום דבר אחר.
   */
  hideWhenRouteDisabled?: boolean;
  /**
   * If set, this item is shown only when the feature flag with this key is enabled
   * for the current fund + user (evaluated via the FeatureFlagsProvider).
   */
  featureKey?: string;
  /**
   * הסתרה מלאה כשה-featureKey כבוי, ולא הטיפול הרגיל של פריט מנוטרל (מוצג
   * מטושטש ולא לחיץ).
   *
   * למה זה נחוץ בנפרד מ-featureKey: פריט עם דגל כבוי נשאר בתפריט מטושטש - וזה
   * מכוון לפיצ'ר שעוד לא יצא, כהצגה של מה שעוד יבוא. אבל פיצ'ר שהוסר בבקשת
   * המשרד הוא לא הבטחה לעתיד: הוא לא צריך להשאיר בתפריט כתם מטושטש שמזמין
   * שאלות. אותו שיקול בדיוק כמו ב-hideWhenRouteDisabled, רק שהשער הוא הדגל.
   */
  hideWhenFeatureOff?: boolean;
  /**
   * הסתרה כשאחד הדגלים האלה דלוק: לפריט שמייצג את «מה שהיה» עד שהחדש נפתח.
   *
   * HAT-1231: קבוצת «הגדרות קרן» המעומעמת נשארת בתפריט כל עוד שני הדגלים
   * סגורים - כך נראה התפריט לפני הפיצ'ר אצל מי שאין לו גישה. ברגע שאחד מהם
   * נפתח היא מפנה את מקומה לשני הפריטים החדשים.
   */
  hideWhenAnyFeatureOn?: readonly string[];
  /**
   * ההשלמה של hideWhenAnyFeatureOn: פריט שמופיע רק כשאחד הדגלים האלה דלוק,
   * ובלי קשר לדגל featureKey שלו, שממשיך לקבוע רק אם הוא מעומעם או לחיץ.
   */
  hideUnlessAnyFeatureOn?: readonly string[];
  /**
   * דגלים נוספים שגם הם פותחים את הפריט (לחיץ ולא מעומעם), בנוסף ל-featureKey.
   *
   * HAT-1231: «אישורים ומסמכים» נפתח בדגל משלו לכל הרכזים, אבל גם מי שיש לו
   * fund-settings כבר רואה אותו - זה היה חלק ממסך הגדרות הקרן שלו לפני הפיצול.
   */
  alsoEnabledBy?: readonly string[];
}

export const sidebarItems: SidebarItem[] = [
  {
    text: 'managementArea.title',
    to: '/management',
    adminOnly: true,
    debuggerOnly: true,
  },
  /*
   * "תמונת מצב" — פריט אחד, מאחורי דגל התכונה 'dashboard'.
   *
   * הפריט ישב כאן מאז שהתפריט נבנה והצביע לנתיב שלא היה קיים, ולכן הוצג
   * מעומעם ולא לחיץ לכל הרכזים. עכשיו יש לו מסך, והדגל הוא שקובע מי רואה
   * אותו: ההנהלה משחררת ממסך ניהול הדגלים, בלי פריסה. קדמה לדגל רשימת
   * מיילים בקוד (allowedEmails + dashboardAccess.ts) שהוסרה.
   *
   * hideWhenFeatureOff כדי שלרכז שהמסך עוד לא שוחרר אליו לא תישאר רשומה
   * מתה בתפריט. hideWhenRouteDisabled אינו נחוץ כאן: לפריט עם דגל, הדגל
   * הוא הקובע ורשימת הנתיבים אינה נבדקת.
   */
  {
    text: 'dashboard',
    iconSrc: icStatusImage,
    activeIconSrc: icStatusImageActive,
    to: 'dashboard',
    featureKey: 'dashboard',
    hideWhenFeatureOff: true,
  },
  {
    text: 'transactionsBoard',
    iconSrc: icTransactions,
    activeIconSrc: icTransactionsActive,
    to: 'transactions',
  },
  {
    text: 'support',
    iconSrc: icSupport,
    activeIconSrc: icSupportActive,
    // On the group while it is closed, which it opens as
    badgeKey: 'israeliIdChangeRequests',
    badgeIcon: idIcon,
    badgeTooltip: 'israeliIdChangeRequest.navBadge',
    children: [
      { text: 'supportRequests', to: 'support/support-requests' },
      // HAT-1428: open ID correction requests - waiting, or decided and not yet seen - with the
      // ID-card icon the coordinator sends the request from (Chaya chose it, 20.10)
      {
        text: 'supporteds',
        to: 'support/supporteds',
        badgeKey: 'israeliIdChangeRequests',
        badgeIcon: idIcon,
        badgeTooltip: 'israeliIdChangeRequest.navBadge',
      },
      { text: 'supportGoals', to: 'support/goals' },
    ],
  },
  /*
   * מיזמים - סעיף משלו בתפריט, עם שני תתי-טאבים: מיזמים וספקים.
   *
   * האפיון של דביר מציב את הכניסה רק באריח "הוצאות מיזם" שבמסך מטרות התמיכה, וכך זה נבנה. המשרד
   * ראה את זה באוויר ב-24/09/2026 וביקש אחרת ("זה אמור להיות בנפרד כמו בקשות תמיכה"), וב-22/10
   * פירט: "מתחת לטאב מיזמים צריך 2 תתי טאבים - מיזמים, ספקים - כמו בבקשות תמיכה". הכניסה מהאריח
   * נשארה - היא לא מפריעה ומופיעה באפיון - וזו תוספת לידה.
   *
   * הנתיבים הם projects/list ו-projects/suppliers, ולא תחת support/goals: NavLink מסמן כל פריט
   * שהנתיב שלו הוא רישא של הכתובת, ולכן כשהמיזמים ישבו ב-support/goals/projects נדלקו "מטרות
   * תמיכה" ו"מיזמים" יחד (רואים את זה בצילומי המסך של המשרד). שלושת הנתיבים הישנים מפנים לחדשים.
   *
   * הדגל והסתרתו נקבעים על כל פריט ועל הקבוצה, בדיוק כמו בקבוצת "תרומות" - ילד בלי הדגל היה
   * נשאר לחיץ בקבוצה שנראית כבויה.
   */
  {
    text: 'projects.sidebarTitle',
    iconSrc: icProjects,
    activeIconSrc: icProjectsActive,
    featureKey: 'projects',
    hideWhenFeatureOff: true,
    children: [
      { text: 'projects.sidebar.projectsTab', to: 'projects/list', featureKey: 'projects', hideWhenFeatureOff: true },
      { text: 'projects.sidebar.suppliersTab', to: 'projects/suppliers', featureKey: 'projects', hideWhenFeatureOff: true },
    ],
  },
  /*
   * «כוללים» (HAT-1446): the same move the office asked for on «פרויקטים» above, for the same
   * reason - the office found the tile-only entry under «מטרות תמיכה» "a bit cumbersome"
   * (25.10.2026, HAT-1446's own board thread). The tile stays; this is a direct way in next to it.
   *
   * kollels-sidebar.svg is jewish.svg (the tile's icon) redrawn in the menu's style, like «תמיכה»:
   * one colour with a bordeaux active pair, a 1px stroke and ~18px of drawing in the 24px box
   * (HAT-10169). The tile keeps the two-colour original.
   */
  {
    text: 'kollels.sidebarTitle',
    iconSrc: icKollels,
    activeIconSrc: icKollelsActive,
    to: 'support/goals/kollels',
    featureKey: 'kollels',
    hideWhenFeatureOff: true,
  },
  /*
   * «הוראות קבע» (HAT-1135): while only the credit screen is released, the item is flat, as it
   * has always been. Once the bank screen's flag opens for a user the item becomes a group with
   * two children - «אשראי» and «בנקאי» - the way «תמיכה» and «תרומות» are laid out (Chaya, 23.9.2026:
   * sub-items in the menu, not tabs inside the screen). Same pair of switches as the documents item.
   */
  {
    text: 'debits',
    iconSrc: icDebits,
    activeIconSrc: icDebitsActive,
    to: 'standing-orders',
    featureKey: 'standing-orders',
    hideWhenAnyFeatureOn: ['bank-standing-orders'],
  },
  {
    text: 'standingOrdersGroup',
    iconSrc: icDebits,
    activeIconSrc: icDebitsActive,
    hideUnlessAnyFeatureOn: ['bank-standing-orders'],
    children: [
      { text: 'standingOrdersCredit', to: 'standing-orders', featureKey: 'standing-orders' },
      { text: 'standingOrdersBank', to: 'bank-standing-orders', featureKey: 'bank-standing-orders' },
    ],
  },
  {
    text: 'donations.admin.sidebarTitle',
    iconSrc: icDonations,
    activeIconSrc: icDonationsActive,
    featureKey: 'donations',
    children: [
      { text: 'donations.admin.sidebar.settings', to: 'donations/settings', featureKey: 'donations' },
      { text: 'donations.admin.sidebar.pending', to: 'donations/offline', featureKey: 'donations' },
      { text: 'donations.admin.sidebar.new', to: 'donations/new', featureKey: 'donations' },
    ],
  },
  {
    text: 'additives',
    iconSrc: icAdditives,
    activeIconSrc: icAdditivesActive,
    children: [
      { text: 'rechargeableCards', to: 'addons/rechargeable-cards' },
      { text: 'vouchers', to: 'addons/vouchers' },
      // HAT-1434: hidden, not blurred, until the office releases it - a live screen, not a teaser.
      { text: 'paymentDevices.sidebar', to: 'addons/devices', featureKey: 'device-rentals', hideWhenFeatureOff: true },
    ],
  },
  /*
   * HAT-1231: «הגדרות קרן» הייתה קבוצה עם שלושה תת-פריטים - פרטי קרן, אישורים
   * ומסמכים, ומשתמשים והרשאות - והרכזים הופיעו בשניים מהם. עכשיו זה פריט אחד,
   * «הגדרות», שמחזיק את כל מה שנקבע לקרן; «משתמשים והרשאות» נבלע בתוכו.
   *
   * «אישורים ומסמכים» יצא מהקבוצה והוא פריט ראשי משלו (הכרעת שרי, 15.9): אישור
   * ניהול תקין וטופס הו״ק לא מגדירים דבר בקרן - הם מה שהרכז מוריד ומוסר לתורם
   * ולרואה החשבון. מי שמחפש אישור לא צריך לדעת שהוא «בהגדרות».
   */
  /*
   * A cog, not the sliders that fundSettings.svg draws (Chaya, 17.9): a cog is what «הגדרות» looks
   * like everywhere, and sliders read as filters. fundSettings.svg itself is left alone - the
   * fund blade in the management area and the open-fund request's intro screen both use it.
   */
  /*
   * Documents above settings: an approval for a donor's accountant is asked for weekly, the
   * settings are opened once a year. Each has a flag of its own (decision 55, 22.9) - the
   * documents screen is read-only and useful to every coordinator from day one, while the
   * settings screen waits for the office to designate founders.
   *
   * The menu follows the content (Chaya, 4.10). Whoever has fund-settings gets the new screens,
   * so they get the new items too: an old group whose «פרטי קרן» opened a page titled «הגדרות
   * הקרן», and whose «משתמשים והרשאות» redirected to that same page, read as two items for one
   * page. The four states:
   *
   *   both closed            the blurred legacy group below - the menu as it was before
   *   fund-settings only     «אישורים ומסמכים» + «הגדרות», both open
   *   fund-documents only    «אישורים ומסמכים» open, «הגדרות» blurred
   *   both open              both open
   */
  /*
   * The blurred «הגדרות קרן» group with its three sub-items, as everyone without the feature saw
   * it before the split. It only ever shows with both flags closed, so it is never clickable -
   * its sub-items are there for the look, not to lead anywhere.
   */
  {
    text: 'fundSettingsLegacy',
    iconSrc: icFundSettings,
    activeIconSrc: icFundSettingsActive,
    featureKey: 'fund-settings',
    hideWhenAnyFeatureOn: ['fund-settings', 'fund-documents'],
    children: [
      { text: 'fundInfo', to: 'fund-settings/info', featureKey: 'fund-settings' },
      { text: 'approvalsAndDocuments', to: 'fund-settings/documents', featureKey: 'fund-settings' },
      { text: 'usersAndPermissions', to: 'fund-settings/users', featureKey: 'fund-settings' },
    ],
  },
  {
    text: 'approvalsAndDocuments',
    iconSrc: icApprovalsDocuments,
    activeIconSrc: icApprovalsDocumentsActive,
    to: 'fund-settings/documents',
    featureKey: 'fund-documents',
    alsoEnabledBy: ['fund-settings'],
    hideUnlessAnyFeatureOn: ['fund-documents', 'fund-settings'],
  },
  {
    text: 'fundSettings',
    iconSrc: icSettingsGear,
    activeIconSrc: icSettingsGearActive,
    to: 'fund-settings/info',
    featureKey: 'fund-settings',
    hideUnlessAnyFeatureOn: ['fund-settings', 'fund-documents'],
  },
  /*
   * «גישת API» (HAT-10063): a coordinator's personal API keys for the fund. Hidden, not blurred,
   * until the office opens 'public-api' for the fund - it is offered to one fund at a time.
   */
  {
    text: 'fundApiKeys.sidebar',
    iconSrc: icSettingsGear,
    activeIconSrc: icSettingsGearActive,
    to: 'fund-settings/api-keys',
    featureKey: 'public-api',
    hideWhenFeatureOff: true,
  },
  /*
   * The coordinator's inquiries, promoted out of "support" into the top-level slot that was
   * already reserved for them - icon, label and position all existed, pointing at a
   * 'correspondence' route that was never built and is enabled nowhere.
   *
   * Three reasons to move it rather than leave it as support's fourth child: an inquiry is
   * opened on a transaction or a supported just as often as on a support request, so
   * "support" was never its home; the unread dot is only visible once someone opens the
   * group, which is too late to be a signal; and the Nedarim app reaches the same screen
   * from a button of its own, so the two now match.
   */
  {
    text: 'correspondences',
    iconSrc: icCorrespondences,
    activeIconSrc: icCorrespondencesActive,
    to: 'support/inquiries',
    badgeKey: 'userInquiries',
    /*
     * מוסר מהתפריט בבקשת המשרד: המזכירות לא עובדות את התור, ופנייה שנפתחת לא
     * מגיעה לאף אחד. הדגל 'inquiries' נזרע NotReleased, ולכן הפריט לא מוצג
     * בשום אתר עד שמדליקים אותו במסך הפיצ'רים - בלי פריסה.
     */
    featureKey: 'inquiries',
    hideWhenFeatureOff: true,
  },
  {
    text: 'help',
    iconSrc: icHelp,
    activeIconSrc: icHelpActive,
    to: 'help',
  },
];
